import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, Legend
} from "recharts";
import {
  FileJson, FileText, FileDown, Download, ChevronDown,
  Sparkles, Zap, Shield, Layers, GitBranch, Building2, ArrowUpRight, ArrowLeft,
} from "lucide-react";
import { useAuth } from "../components/AuthContext";
import "../App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const fmtUSD = (n) => `$${Number(n).toLocaleString()}`;

const SECTIONS = [
  { id: "cover", label: "Cover" },
  { id: "executive-summary", label: "Summary" },
  { id: "cost-breakdown", label: "Cost" },
  { id: "timeline", label: "Timeline" },
  { id: "modules", label: "Modules" },
  { id: "ai-tools", label: "AI Stack" },
  { id: "tech-stack", label: "Tech" },
  { id: "personas", label: "Personas" },
  { id: "user-stories", label: "Stories" },
  { id: "data-models", label: "Schema" },
  { id: "apis", label: "APIs" },
  { id: "nfr", label: "NFRs" },
  { id: "risks", label: "Risks" },
  { id: "roadmap", label: "Roadmap" },
];

// Module-level constants
const CHART_TICK = { fontSize: 11, fill: "#5E6B82" };
const CHART_CURSOR = { fill: "#E6EAF0" };
const CHART_TOOLTIP_STYLE = {
  background: "#1A2332", border: "none", borderRadius: 4, color: "#FFFFFF", fontSize: 12,
};
const BAR_CHART_MARGIN = { top: 12, right: 20, left: 12, bottom: 80 };
const BAR_CHART_HEIGHT = { width: "100%", height: 380, minHeight: 380 };
const PIE_CHART_HEIGHT = { width: "100%", height: 260, minHeight: 260 };
const PIE_LEGEND_STYLE = { fontSize: 12 };
const PIE_COLORS = ["#B91C1C", "#0F766E", "#1A2332", "#5E6B82", "#D2D8E1"];
const BAR_RADIUS = [2, 2, 0, 0];
const INTERSECTION_OBSERVER_OPTS = {
  rootMargin: "-20% 0px -60% 0px",
  threshold: [0.1, 0.25, 0.5],
};
const TECH_GROUPS = [
  { key: "frontend", label: "Frontend", icon: <Layers size={16} /> },
  { key: "backend", label: "Backend", icon: <Zap size={16} /> },
  { key: "ai", label: "AI / ML", icon: <Sparkles size={16} /> },
  { key: "infra", label: "Infrastructure", icon: <Building2 size={16} /> },
  { key: "integrations", label: "Integrations", icon: <GitBranch size={16} /> },
  { key: "security", label: "Security & Compliance", icon: <Shield size={16} /> },
];
const PRIORITIES = ["ALL", "P0", "P1", "P2"];
const TAKEAWAYS = [
  { id: "leverage", icon: <Sparkles size={18} />, title: "AI is the leverage", body: "Cost compression comes from collapsing eng + design + QA into a single founder loop. Build velocity is 3–4× a traditional team." },
  { id: "trust",    icon: <Shield size={18} />,   title: "Trust is the moat",   body: "Accountants are risk-averse. SOC2, audit logs, and white-glove onboarding matter more than a perfect feature list." },
  { id: "wedge",    icon: <GitBranch size={18} />, title: "Workflows are the wedge", body: "Automators + email-as-work are the depth incumbents miss. Plan to spend ~20% of the build there." },
];

// =================== Top nav ===================

function NavChip({ section, active, onJump }) {
  return (
    <button
      className={`nav-chip ${active === section.id ? "active" : ""}`}
      onClick={() => onJump(section.id)}
      data-testid={`nav-${section.id}`}
    >
      {section.label}
    </button>
  );
}

// Close popover/menu when clicking outside its ref'd element
function useOutsideClick(ref, onOutside) {
  useEffect(() => {
    const handler = (event) => {
      if (ref.current && !ref.current.contains(event.target)) onOutside();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [ref, onOutside]);
}

function DownloadMenu({ onMD, onJSON, onHTML, onPrint }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  useOutsideClick(ref, close);

  const wrap = (fn) => () => { fn(); close(); };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="btn-primary"
        data-testid="download-menu-trigger"
      >
        <Download size={14} /> Download PRD <ChevronDown size={12} />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-64 card shadow-xl overflow-hidden z-40"
          data-testid="download-menu"
          style={{ boxShadow: "0 16px 40px -16px rgba(26,24,20,0.25)" }}
        >
          <DownloadOption icon={<FileDown size={14} />} label="Print to PDF"
            sub="Opens print dialog · A4 styled" onClick={wrap(onPrint)} testid="download-pdf-button" />
          <DownloadOption icon={<FileText size={14} />} label="Styled HTML"
            sub="Self-contained · save as PDF" onClick={wrap(onHTML)} testid="download-html-button" />
          <DownloadOption icon={<FileText size={14} />} label="Markdown (.md)"
            sub="For Notion, GitHub, docs" onClick={wrap(onMD)} testid="download-md-button" />
          <DownloadOption icon={<FileJson size={14} />} label="Raw JSON"
            sub="For pipelines & tooling" onClick={wrap(onJSON)} testid="download-json-button" />
        </div>
      )}
    </div>
  );
}

function DownloadOption({ icon, label, sub, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-start gap-3 px-4 py-3 hover:bg-[var(--subtle)] transition text-left border-b border-[var(--border)] last:border-0"
      data-testid={testid}
    >
      <span className="mt-0.5 text-[var(--accent)]">{icon}</span>
      <span className="flex-1">
        <span className="block text-[13px] font-medium text-[var(--ink)]">{label}</span>
        <span className="block text-[11px] text-[var(--ink-3)] mt-0.5">{sub}</span>
      </span>
    </button>
  );
}

function ModeToggle({ mode, setMode }) {
  return (
    <div
      className="hidden md:inline-flex items-center bg-[var(--paper)] border border-[var(--border)] rounded-full p-0.5 text-sm"
      data-testid="mode-toggle"
    >
      <button
        onClick={() => setMode("ai")}
        className={`px-3 py-1 rounded-full transition ${mode === "ai" ? "bg-[var(--ink)] text-[var(--paper)]" : "text-[var(--ink-2)] hover:text-[var(--ink)]"}`}
        data-testid="mode-ai"
      >Solo + AI</button>
      <button
        onClick={() => setMode("trad")}
        className={`px-3 py-1 rounded-full transition ${mode === "trad" ? "bg-[var(--ink)] text-[var(--paper)]" : "text-[var(--ink-2)] hover:text-[var(--ink)]"}`}
        data-testid="mode-trad"
      >Traditional</button>
    </div>
  );
}

function TopHeader({ mode, setMode, active, onJump, onDownloadMD, onDownloadJSON, onDownloadHTML, onPrint }) {
  const { state } = useAuth();
  const authed = state && state !== false;
  return (
    <header className="sticky top-0 z-30 backdrop-blur bg-[var(--bg)]/85 border-b border-[var(--border)] no-print">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 flex items-center justify-center font-heading italic text-lg"
            style={{ background: "var(--ink)", color: "var(--paper)", borderRadius: "50%" }}
          >
            p
          </div>
          <div className="leading-tight">
            <div className="text-[13px] font-medium text-[var(--ink)]">Practice OS · Build Brief</div>
            <div className="overline text-[9px]">By Emergent · Jan 2026</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {authed && (
            <Link to="/dashboard" className="btn-ghost" data-testid="brief-back-to-portal">
              <ArrowLeft size={14} /> Back to portal
            </Link>
          )}
          <ModeToggle mode={mode} setMode={setMode} />
          <DownloadMenu onMD={onDownloadMD} onJSON={onDownloadJSON} onHTML={onDownloadHTML} onPrint={onPrint} />
        </div>
      </div>
      {/* Chapter strip */}
      <nav
        className="border-t border-[var(--border)] bg-[var(--bg)]"
        data-testid="chapter-nav"
      >
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-2 flex items-center gap-1 overflow-x-auto scrollbar-hide">
          <span className="overline mr-3 shrink-0">Chapters</span>
          {SECTIONS.map((s) => (
            <NavChip key={s.id} section={s} active={active} onJump={onJump} />
          ))}
        </div>
      </nav>
    </header>
  );
}

// =================== Sections ===================

function Cover({ meta, summary }) {
  return (
    <section
      id="cover"
      className="relative pt-20 pb-28 border-b border-[var(--border)] paper-noise"
      data-testid="cover-section"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
        <div className="lg:col-span-8">
          <div className="flex items-center gap-3 mb-10">
            <span className="badge badge-outline">A Field Brief</span>
            <span className="overline">Vol. 01 · Issue 01</span>
          </div>
          <h1 className="font-heading text-[52px] md:text-[84px] leading-[0.96] tracking-tight text-[var(--ink)]" style={{ fontWeight: 500 }}>
            <span className="chapter-num text-[var(--accent)] mr-3">{meta?.codename || "Practice OS"}</span>
            <br />
            a modern <em className="italic" style={{ color: "var(--accent)" }}>practice management</em> platform.
          </h1>
          <p className="mt-10 text-xl text-[var(--ink-2)] leading-[1.55] max-w-2xl">
            A solo-founder cost & timeline study, plus an enterprise-grade Product Requirements Document
            covering the sixteen core modules of a modern accounting practice — from <em>AI Agents</em> to
            <em> Workflow Automation</em>.
          </p>
          <div className="mt-12 inline-flex items-center gap-2 text-[13px] text-[var(--ink-2)] border-t border-[var(--ink)] pt-3" style={{ borderTopWidth: 2 }}>
            <span className="font-mono">{meta?.date}</span>
            <span>·</span>
            <span>{meta?.author}</span>
            <span>·</span>
            <span>v{meta?.version}</span>
          </div>
        </div>

        <aside className="lg:col-span-4 lg:pl-8 lg:border-l border-[var(--border)]">
          <div className="overline mb-3">The TL;DR</div>
          <p className="pull-quote">{summary.tldr}</p>
        </aside>
      </div>
    </section>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="bg-[var(--paper)] p-6 md:p-8">
      <div className="overline mb-3">{label}</div>
      <div className="font-heading text-3xl md:text-4xl text-[var(--ink)] tabular leading-tight" style={{ fontWeight: 500 }}>{value}</div>
      <div className="text-[13px] text-[var(--ink-3)] mt-3 leading-relaxed">{sub}</div>
    </div>
  );
}

function KeyTakeaway({ icon, title, body }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-[var(--accent)] mb-2">
        {icon}
        <div className="font-medium text-[var(--ink)] text-sm">{title}</div>
      </div>
      <p className="text-sm text-[var(--ink-2)] leading-relaxed">{body}</p>
    </div>
  );
}

function ExecutiveSummary({ data, mode }) {
  const s = data.executive_summary;
  const isAI = mode === "ai";
  const cost = isAI ? s.headline_cost_ai : s.headline_cost_traditional;
  const weeks = isAI ? s.headline_time_ai_weeks : s.headline_time_traditional_weeks;

  return (
    <section id="executive-summary" className="py-24 border-b border-[var(--border)]" data-testid="exec-summary-section">
      <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
        <div>
          <div className="overline mb-3"><span className="chapter-num text-base mr-2">01</span> Executive Summary</div>
          <h2 className="section-title text-4xl md:text-5xl">The numbers, up front.</h2>
        </div>
        <div className="text-sm text-[var(--ink-2)] max-w-sm">
          Showing <span className="kbd">{isAI ? "Solo + AI" : "Traditional Team"}</span> scenario.
          Toggle in the header to switch.
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--border)] border border-[var(--border)] rounded-md overflow-hidden">
        <Stat label="All-in Build Cost" value={`${fmtUSD(cost.low)} – ${fmtUSD(cost.high)}`} sub={isAI ? "Inclusive of AI tool subscriptions, infra, and integrations" : "6–10 person product team"} />
        <Stat label="Time to Private Beta" value={`${weeks.low}–${weeks.high} wks`} sub={isAI ? "Roughly 5.5 – 8.5 months" : "Roughly 18 – 30 months"} />
        <Stat label="Modules at Parity" value="16 / 16" sub="Functional parity, not pixel-perfect parity" />
        <Stat label="Team Size" value={isAI ? "1" : "6–10"} sub={s.team_assumption} />
      </div>

      <div className="mt-12 card p-8 grid grid-cols-1 md:grid-cols-3 gap-8">
        {TAKEAWAYS.map((t) => (
          <KeyTakeaway key={t.id} icon={t.icon} title={t.title} body={t.body} />
        ))}
      </div>
    </section>
  );
}

function SummaryTile({ title, value, sub, accent }) {
  const tileStyle = {
    background: accent ? "var(--ink)" : "var(--paper)",
    borderColor: accent ? "var(--ink)" : "var(--border)",
    color: accent ? "var(--paper)" : "var(--ink)",
  };
  const overlineStyle = accent ? { color: "rgba(250,246,238,0.6)" } : undefined;
  const subStyle = { color: accent ? "rgba(250,246,238,0.7)" : "var(--ink-3)" };
  return (
    <div className="p-7 border rounded-md" style={tileStyle}>
      <div className="overline" style={overlineStyle}>{title}</div>
      <div className="font-heading text-3xl md:text-4xl mt-3 tabular" style={{ fontWeight: 500 }}>{value}</div>
      {sub && <div className="text-[12px] mt-2" style={subStyle}>{sub}</div>}
    </div>
  );
}

function CostChart({ chartData, mode }) {
  const dataKey = mode === "ai" ? "ai" : "trad";
  const barFill = mode === "ai" ? "#B91C1C" : "#1A2332";
  return (
    <div style={BAR_CHART_HEIGHT}>
      <ResponsiveContainer>
        <BarChart data={chartData} margin={BAR_CHART_MARGIN}>
          <CartesianGrid stroke="#D2D8E1" vertical={false} />
          <XAxis dataKey="name" angle={-30} textAnchor="end" interval={0} tick={CHART_TICK} height={80} />
          <YAxis tick={CHART_TICK} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
          <Tooltip
            cursor={CHART_CURSOR}
            contentStyle={CHART_TOOLTIP_STYLE}
            formatter={(v, _n, p) => [fmtUSD(v), p?.payload?.full]}
            labelFormatter={() => ""}
          />
          <Bar dataKey={dataKey} radius={BAR_RADIUS}>
            {chartData.map((row) => <Cell key={row.full} fill={barFill} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function PhasesTable({ phases }) {
  return (
    <div className="mt-8 card overflow-hidden">
      <table className="report-table">
        <thead>
          <tr>
            <th>Phase</th>
            <th>Weeks</th>
            <th>AI Build</th>
            <th>Traditional</th>
            <th>Focus</th>
          </tr>
        </thead>
        <tbody>
          {phases.map((p) => (
            <tr key={p.phase} data-testid={`phase-row-${p.phase}`}>
              <td className="text-[var(--ink)] font-medium">{p.phase}</td>
              <td className="tabular">{p.weeks}</td>
              <td className="tabular text-[var(--accent)] font-medium">{fmtUSD(p.ai_cost)}</td>
              <td className="tabular">{fmtUSD(p.trad_cost)}</td>
              <td>{p.focus}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CostBreakdown({ data, mode }) {
  const chartData = data.phases.map((p) => ({
    name: p.phase.length > 26 ? p.phase.slice(0, 26) + "…" : p.phase,
    full: p.phase,
    ai: p.ai_cost,
    trad: p.trad_cost,
    weeks: p.weeks,
  }));
  const totalAI = data.phases.reduce((a, b) => a + b.ai_cost, 0);
  const totalTrad = data.phases.reduce((a, b) => a + b.trad_cost, 0);
  const totalWeeks = data.phases.reduce((a, b) => a + b.weeks, 0);
  const reductionPct = Math.round((1 - totalAI / totalTrad) * 100);

  return (
    <section id="cost-breakdown" className="py-24 border-b border-[var(--border)]" data-testid="cost-section">
      <div className="overline mb-3"><span className="chapter-num text-base mr-2">02</span> Cost & Phases</div>
      <h2 className="section-title text-4xl md:text-5xl mb-3">Where the money goes.</h2>
      <p className="text-[var(--ink-2)] max-w-3xl mb-12 text-lg">
        Nine phases spanning {totalWeeks} weeks. Core MVP, Workflows + Portal, and AI Agents
        together consume about 55% of the AI-build cost.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-10">
        <SummaryTile title="AI-Build Total" value={fmtUSD(totalAI)} accent />
        <SummaryTile title="Traditional Total" value={fmtUSD(totalTrad)} />
        <SummaryTile title="Cost Reduction" value={`${reductionPct}%`} accent sub="AI vs. traditional" />
      </div>

      <div className="card p-6">
        <div className="overline mb-4">Cost by Phase ({mode === "ai" ? "Solo + AI" : "Traditional"})</div>
        <CostChart chartData={chartData} mode={mode} />
      </div>

      <PhasesTable phases={data.phases} />
    </section>
  );
}

function GanttRow({ row, totalWeeks }) {
  const barStyle = {
    marginLeft: `${(row.start / totalWeeks) * 100}%`,
    width: `${(row.weeks / totalWeeks) * 100}%`,
  };
  return (
    <div className="gantt-row" data-testid={`gantt-${row.phase}`}>
      <div>
        <div className="text-[13px] text-[var(--ink)] font-medium leading-tight">{row.phase}</div>
        <div className="text-[11px] text-[var(--ink-3)] font-mono mt-0.5">
          W{row.start + 1}–W{row.end} · {row.weeks}w
        </div>
      </div>
      <div className="gantt-track">
        <div className="gantt-bar" style={barStyle} title={`${row.phase}: weeks ${row.start + 1}-${row.end}`} />
      </div>
    </div>
  );
}

function GanttHeader({ totalWeeks }) {
  const marks = Array.from({ length: Math.ceil(totalWeeks / 4) }, (_, i) => i * 4 + 1);
  return (
    <div className="gantt-row" style={{ borderBottomWidth: 2, borderBottomColor: "var(--ink)" }}>
      <div className="overline">Phase</div>
      <div className="flex justify-between text-[10px] text-[var(--ink-3)] font-mono">
        {marks.map((w) => <span key={`mark-${w}`}>W{w}</span>)}
        <span key="mark-end">W{totalWeeks}</span>
      </div>
    </div>
  );
}

function Timeline({ data }) {
  const phases = data.phases;
  const totalWeeks = phases.reduce((a, b) => a + b.weeks, 0);
  let cursor = 0;
  const rows = phases.map((p) => {
    const start = cursor;
    cursor += p.weeks;
    return { ...p, start, end: cursor };
  });

  return (
    <section id="timeline" className="py-24 border-b border-[var(--border)]" data-testid="timeline-section">
      <div className="overline mb-3"><span className="chapter-num text-base mr-2">03</span> Roadmap</div>
      <h2 className="section-title text-4xl md:text-5xl mb-3">A {totalWeeks}-week build, week by week.</h2>
      <p className="text-[var(--ink-2)] max-w-3xl mb-12 text-lg">
        Optimistic-but-realistic schedule assuming the founder is full-time, AI-tools fluent,
        and defers SOC2 Type II + on-prem to Year 2.
      </p>

      <div className="card p-6">
        <div className="overline mb-4 flex justify-between">
          <span>Phase Gantt · Weeks 1–{totalWeeks}</span>
          <span className="font-mono text-[var(--ink-3)]">{totalWeeks} weeks · ~{Math.round(totalWeeks / 4.33)} months</span>
        </div>
        <div className="mt-2">
          <GanttHeader totalWeeks={totalWeeks} />
          {rows.map((row) => <GanttRow key={row.phase} row={row} totalWeeks={totalWeeks} />)}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-8">
        {data.roadmap_quarters.map((q) => (
          <div key={q.q} className="card p-6">
            <div className="overline">{q.q} · Weeks {q.weeks}</div>
            <div className="mt-2 text-[15px] text-[var(--ink)] leading-snug">{q.theme}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function getModuleBadgeClass(priority) {
  if (priority === "P0") return "badge badge-accent";
  if (priority === "P1") return "badge";
  return "badge badge-outline";
}

function ModuleCard({ mod }) {
  const badgeClass = getModuleBadgeClass(mod.priority);
  return (
    <div className="module-card" data-testid={`module-${mod.key}`}>
      <div className="flex items-center justify-between mb-3">
        <span className={badgeClass}>{mod.priority}</span>
        <span className="overline">{mod.complexity}</span>
      </div>
      <h3 className="font-heading text-xl text-[var(--ink)] leading-tight mb-2" style={{ fontWeight: 500 }}>{mod.name}</h3>
      <p className="text-sm text-[var(--ink-2)] leading-relaxed mb-5">{mod.summary}</p>

      <div className="grid grid-cols-2 gap-3 mb-4 pb-4 border-b border-[var(--border)]">
        <div>
          <div className="overline" style={{ fontSize: "9px" }}>AI Build</div>
          <div className="font-mono text-[15px] text-[var(--accent)] mt-1">{mod.effort_weeks_ai}w · {fmtUSD(mod.ai_cost_usd)}</div>
        </div>
        <div>
          <div className="overline" style={{ fontSize: "9px" }}>Traditional</div>
          <div className="font-mono text-[15px] text-[var(--ink-3)] mt-1">{mod.effort_weeks_trad} weeks</div>
        </div>
      </div>

      <div className="overline mb-1.5" style={{ fontSize: "9px" }}>Stack</div>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {mod.ai_stack.map((s) => <span key={`${mod.key}-stack-${s}`} className="kbd">{s}</span>)}
      </div>

      {mod.dependencies.length > 0 && (
        <>
          <div className="overline mb-1" style={{ fontSize: "9px" }}>Depends on</div>
          <div className="text-[12px] text-[var(--ink-2)]">{mod.dependencies.join(" · ")}</div>
        </>
      )}
    </div>
  );
}

function PriorityFilter({ filter, setFilter }) {
  return (
    <div className="inline-flex items-center gap-1 bg-[var(--paper)] border border-[var(--border)] rounded-full p-0.5">
      {PRIORITIES.map((p) => (
        <button
          key={p}
          onClick={() => setFilter(p)}
          className={`px-3.5 py-1.5 text-xs font-mono uppercase tracking-wider rounded-full transition ${filter === p ? "bg-[var(--ink)] text-[var(--paper)]" : "text-[var(--ink-2)] hover:text-[var(--ink)]"}`}
          data-testid={`filter-${p}`}
        >{p}</button>
      ))}
    </div>
  );
}

function Modules({ data }) {
  const [filter, setFilter] = useState("ALL");
  const mods = data.modules.filter((m) => filter === "ALL" || m.priority === filter);

  return (
    <section id="modules" className="py-24 border-b border-[var(--border)]" data-testid="modules-section">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
        <div>
          <div className="overline mb-3"><span className="chapter-num text-base mr-2">04</span> Modules</div>
          <h2 className="section-title text-4xl md:text-5xl">All sixteen modules.</h2>
          <p className="text-[var(--ink-2)] mt-3 max-w-2xl text-lg">From AI Agents to Workflow Automation. Each module estimated independently.</p>
        </div>
        <PriorityFilter filter={filter} setFilter={setFilter} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {mods.map((m) => <ModuleCard key={m.key} mod={m} />)}
      </div>
    </section>
  );
}

function ToolsTable({ tools, total }) {
  return (
    <div className="lg:col-span-2 card overflow-hidden">
      <table className="report-table">
        <thead>
          <tr>
            <th>Tool</th><th>Purpose</th><th>Category</th><th className="text-right">Monthly</th>
          </tr>
        </thead>
        <tbody>
          {tools.map((t) => (
            <tr key={t.tool}>
              <td className="text-[var(--ink)] font-medium">{t.tool}</td>
              <td>{t.purpose}</td>
              <td><span className="badge badge-outline">{t.category}</span></td>
              <td className="text-right tabular">{t.monthly_usd === 0 ? "Usage-based" : `$${t.monthly_usd}`}</td>
            </tr>
          ))}
          <tr>
            <td colSpan="3" className="!text-[var(--ink)] font-semibold">Total</td>
            <td className="text-right tabular !text-[var(--accent)] font-semibold">${total}/mo</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function ToolsPie({ pieData }) {
  return (
    <div className="card p-6">
      <div className="overline mb-4">Spend by Category</div>
      <div style={PIE_CHART_HEIGHT}>
        <ResponsiveContainer>
          <PieChart>
            <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={1}>
              {pieData.map((slice, i) => <Cell key={slice.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
            </Pie>
            <Tooltip formatter={(v) => `$${v}/mo`} contentStyle={CHART_TOOLTIP_STYLE} />
            <Legend wrapperStyle={PIE_LEGEND_STYLE} iconSize={8} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function AITools({ data }) {
  const total = data.ai_tools_stack.reduce((a, b) => a + b.monthly_usd, 0);
  const byCategory = data.ai_tools_stack.reduce((acc, t) => {
    acc[t.category] = (acc[t.category] || 0) + t.monthly_usd;
    return acc;
  }, {});
  const pieData = Object.entries(byCategory).map(([name, value]) => ({ name, value }));

  return (
    <section id="ai-tools" className="py-24 border-b border-[var(--border)]" data-testid="ai-tools-section">
      <div className="overline mb-3"><span className="chapter-num text-base mr-2">05</span> AI Tools Stack</div>
      <h2 className="section-title text-4xl md:text-5xl mb-3">The founder's toolbelt.</h2>
      <p className="text-[var(--ink-2)] max-w-3xl mb-10 text-lg">
        Total monthly run-rate: <strong className="text-[var(--ink)]">${total}/mo</strong> &mdash; less than a senior engineer's morning coffee budget.
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <ToolsTable tools={data.ai_tools_stack} total={total} />
        <ToolsPie pieData={pieData} />
      </div>
    </section>
  );
}

function TechStack({ data }) {
  const ts = data.tech_stack;
  return (
    <section id="tech-stack" className="py-24 border-b border-[var(--border)]" data-testid="tech-stack-section">
      <div className="overline mb-3"><span className="chapter-num text-base mr-2">06</span> Tech Stack</div>
      <h2 className="section-title text-4xl md:text-5xl mb-3">An opinionated architecture.</h2>
      <p className="text-[var(--ink-2)] max-w-3xl mb-10 text-lg">Chosen for AI-tool friendliness, deploy velocity, and a credible enterprise story.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {TECH_GROUPS.map((g) => (
          <div key={g.key} className="card p-6">
            <div className="flex items-center gap-2 text-[var(--accent)] mb-3">
              {g.icon}
              <div className="font-medium text-[var(--ink)] text-sm">{g.label}</div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {ts[g.key].map((x) => <span key={`${g.key}-${x}`} className="kbd">{x}</span>)}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function PersonaCard({ persona, index }) {
  return (
    <div className="card p-6">
      <div className="overline mb-2">Persona {String(index + 1).padStart(2, "0")}</div>
      <div className="font-heading text-lg text-[var(--ink)] leading-tight mb-3" style={{ fontWeight: 500 }}>{persona.name}</div>
      <div className="text-[12px] text-[var(--ink-3)] uppercase tracking-wider mt-3 font-mono">Goal</div>
      <div className="text-sm text-[var(--ink-2)] mt-1">{persona.goal}</div>
      <div className="text-[12px] text-[var(--ink-3)] uppercase tracking-wider mt-3 font-mono">Pain</div>
      <div className="text-sm text-[var(--ink-2)] mt-1">{persona.pain}</div>
    </div>
  );
}

function Personas({ data }) {
  return (
    <section id="personas" className="py-24 border-b border-[var(--border)]" data-testid="personas-section">
      <div className="overline mb-3"><span className="chapter-num text-base mr-2">07</span> Personas</div>
      <h2 className="section-title text-4xl md:text-5xl mb-10">Who we're building for.</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
        {data.personas.map((p, i) => <PersonaCard key={p.name} persona={p} index={i} />)}
      </div>
    </section>
  );
}

function UserStoryRow({ story, index }) {
  return (
    <div className="flex gap-5 pb-5 border-b border-[var(--border)]">
      <div className="chapter-num text-3xl leading-none mt-1 tabular shrink-0">
        {String(index + 1).padStart(2, "0")}
      </div>
      <p className="text-[16px] text-[var(--ink-2)] leading-relaxed">{story}</p>
    </div>
  );
}

function UserStories({ data }) {
  return (
    <section id="user-stories" className="py-24 border-b border-[var(--border)]" data-testid="user-stories-section">
      <div className="overline mb-3"><span className="chapter-num text-base mr-2">08</span> User Stories</div>
      <h2 className="section-title text-4xl md:text-5xl mb-10">Core jobs-to-be-done.</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-5 max-w-5xl">
        {data.user_stories.map((u, i) => <UserStoryRow key={u} story={u} index={i} />)}
      </div>
    </section>
  );
}

function DataModels({ data }) {
  return (
    <section id="data-models" className="py-24 border-b border-[var(--border)]" data-testid="data-models-section">
      <div className="overline mb-3"><span className="chapter-num text-base mr-2">09</span> Data Models</div>
      <h2 className="section-title text-4xl md:text-5xl mb-10">Schema, at a glance.</h2>
      <div className="card overflow-hidden">
        <table className="report-table">
          <thead>
            <tr><th>Entity</th><th>Fields</th></tr>
          </thead>
          <tbody>
            {data.data_models.map((m) => (
              <tr key={m.entity}>
                <td className="text-[var(--ink)] font-medium font-mono">{m.entity}</td>
                <td className="font-mono text-[12px]">{m.fields}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function APIs({ data }) {
  return (
    <section id="apis" className="py-24 border-b border-[var(--border)]" data-testid="apis-section">
      <div className="overline mb-3"><span className="chapter-num text-base mr-2">10</span> Key APIs</div>
      <h2 className="section-title text-4xl md:text-5xl mb-10">Surface area.</h2>
      <div className="card p-6 font-mono text-[13px] text-[var(--ink)] leading-loose">
        {data.key_apis.map((a) => (
          <div key={a} className="border-b border-[var(--border)] py-2.5 last:border-0">{a}</div>
        ))}
      </div>
    </section>
  );
}

function NFR({ data }) {
  return (
    <section id="nfr" className="py-24 border-b border-[var(--border)]" data-testid="nfr-section">
      <div className="overline mb-3"><span className="chapter-num text-base mr-2">11</span> Non-Functional Requirements</div>
      <h2 className="section-title text-4xl md:text-5xl mb-10">The boring-but-mission-critical part.</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {data.non_functional.map((n) => (
          <div key={n.category} className="card p-6">
            <div className="overline mb-2">{n.category}</div>
            <div className="text-[15px] text-[var(--ink)] leading-relaxed">{n.target}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function RiskRow({ risk, index }) {
  return (
    <div className="card p-7">
      <div className="flex items-start gap-5">
        <div className="chapter-num text-4xl leading-none mt-1 tabular shrink-0">
          {String(index + 1).padStart(2, "0")}
        </div>
        <div className="flex-1">
          <div className="text-[var(--ink)] font-medium text-[15px] mb-2">{risk.risk}</div>
          <div className="text-[14px] text-[var(--ink-2)]">
            <strong className="text-[var(--accent)]">Mitigation →</strong> {risk.mitigation}
          </div>
        </div>
      </div>
    </div>
  );
}

function Risks({ data }) {
  return (
    <section id="risks" className="py-24 border-b border-[var(--border)]" data-testid="risks-section">
      <div className="overline mb-3"><span className="chapter-num text-base mr-2">12</span> Risks & Mitigations</div>
      <h2 className="section-title text-4xl md:text-5xl mb-10">What can kill this.</h2>
      <div className="space-y-4 max-w-5xl">
        {data.risks.map((r, i) => <RiskRow key={r.risk} risk={r} index={i} />)}
      </div>
    </section>
  );
}

function Roadmap({ data }) {
  return (
    <section id="roadmap" className="py-24 border-b border-[var(--border)]" data-testid="roadmap-section">
      <div className="overline mb-3"><span className="chapter-num text-base mr-2">13</span> Roadmap by Quarter</div>
      <h2 className="section-title text-4xl md:text-5xl mb-10">The year ahead.</h2>
      <div className="relative max-w-5xl">
        <div className="absolute left-4 top-2 bottom-2 w-px bg-[var(--border)]" />
        {data.roadmap_quarters.map((q) => (
          <div key={q.q} className="relative pl-14 pb-10 last:pb-0">
            <div
              className="absolute left-2 top-1 w-5 h-5 rounded-full border-4"
              style={{ background: "var(--accent)", borderColor: "var(--paper)" }}
            />
            <div className="overline">{q.q} · Weeks {q.weeks}</div>
            <div className="font-heading text-2xl text-[var(--ink)] mt-1" style={{ fontWeight: 500 }}>{q.theme}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Footer({ meta, onDownloadHTML, onPrint }) {
  return (
    <footer className="py-20 border-t-2 border-[var(--ink)] mt-12">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-10 items-start">
        <div>
          <div
            className="w-12 h-12 flex items-center justify-center font-heading italic text-xl"
            style={{ background: "var(--ink)", color: "var(--paper)", borderRadius: "50%" }}
          >p</div>
          <div className="font-heading text-2xl mt-4" style={{ fontWeight: 500 }}>{meta?.codename || "Practice OS"}</div>
          <div className="text-[13px] text-[var(--ink-2)] mt-2 max-w-xs">{meta?.subtitle}</div>
          <div className="mt-6 flex gap-2 flex-wrap">
            <button onClick={onDownloadHTML} className="btn-secondary" data-testid="footer-download-html">
              <FileText size={14} /> Styled HTML
            </button>
            <button onClick={onPrint} className="btn-primary" data-testid="footer-download-pdf">
              <FileDown size={14} /> Save as PDF
            </button>
          </div>
        </div>
        <div>
          <div className="overline mb-3">Method</div>
          <p className="text-sm text-[var(--ink-2)] leading-relaxed">
            Estimates based on the public feature surface of leading practice-management
            platforms (Jan 2026), benchmarks of AI-built SaaS shipped in 2024–2026, and a
            founder working 45–55 productive hours/week.
          </p>
        </div>
        <div>
          <div className="overline mb-3">Disclaimer</div>
          <p className="text-sm text-[var(--ink-2)] leading-relaxed">
            Ranges represent a 70% confidence interval. Real costs depend on regulatory
            regime, integrations chosen, and how many category-leading features you decide
            to compromise on. Not investment, legal, or tax advice.
          </p>
        </div>
      </div>
      <div className="mt-14 pt-6 border-t border-[var(--border)] flex flex-wrap justify-between text-[12px] text-[var(--ink-3)] font-mono">
        <div>© 2026 Emergent · {meta?.codename} Brief</div>
        <div>{meta?.date} · v{meta?.version}</div>
      </div>
    </footer>
  );
}

function LoadingState() {
  return (
    <div className="min-h-screen flex items-center justify-center text-[var(--ink-2)]" data-testid="loading-state">
      <div className="text-center">
        <div className="font-heading text-3xl text-[var(--ink)]" style={{ fontWeight: 500 }}>Loading the brief…</div>
        <div className="overline mt-3">Estimating · costing · drafting</div>
      </div>
    </div>
  );
}

function ReportBody({ data, mode, onDownloadHTML, onPrint }) {
  return (
    <>
      <Cover meta={data.meta} summary={data.executive_summary} />
      <ExecutiveSummary data={data} mode={mode} />
      <CostBreakdown data={data} mode={mode} />
      <Timeline data={data} />
      <Modules data={data} />
      <AITools data={data} />
      <TechStack data={data} />
      <Personas data={data} />
      <UserStories data={data} />
      <DataModels data={data} />
      <APIs data={data} />
      <NFR data={data} />
      <Risks data={data} />
      <Roadmap data={data} />
      <Footer meta={data.meta} onDownloadHTML={onDownloadHTML} onPrint={onPrint} />
    </>
  );
}

function useActiveSection(ready) {
  const [active, setActive] = useState("cover");
  useEffect(() => {
    if (!ready) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible.length) setActive(visible[0].target.id);
      },
      INTERSECTION_OBSERVER_OPTS
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
    // SECTIONS + INTERSECTION_OBSERVER_OPTS are module-level constants; ready is the only reactive input.
  }, [ready]);
  return [active, setActive];
}

function Brief() {
  const { state } = useAuth();
  const [data, setData] = useState(null);
  const [mode, setMode] = useState("ai");
  const [active, setActive] = useActiveSection(!!data);

  // Access gate: Build Brief is a private planning doc. Only the platform
  // owner (mannie@ebta.com.au) and superadmins can read it.
  const email = (state?.user?.email || "").toLowerCase();
  const isSuperadmin = !!state?.user?.is_superadmin;
  const canView = isSuperadmin || email === "mannie@ebta.com.au";

  // Fetch the PRD once on mount. API_BASE is a module-level constant.
  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    axios.get(`${API_BASE}/prd`).then((res) => {
      if (!cancelled) setData(res.data);
    }).catch(console.error);
    return () => { cancelled = true; };
  }, [canView]);

  const jump = useCallback((id) => {
    setActive(id);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [setActive]);

  // API_BASE is module-level; window/setTimeout are globals — no deps needed.
  const onDownloadMD = useCallback(() => window.open(`${API_BASE}/prd/markdown`, "_blank"), []);
  const onDownloadJSON = useCallback(() => window.open(`${API_BASE}/prd/json`, "_blank"), []);
  const onDownloadHTML = useCallback(() => window.open(`${API_BASE}/prd/html`, "_blank"), []);
  const onPrint = useCallback(() => {
    const w = window.open(`${API_BASE}/prd/html`, "_blank");
    if (w) w.addEventListener("load", () => setTimeout(() => w.print(), 600));
  }, []);

  if (!canView) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="brief-access-denied">
        <div className="max-w-md text-center">
          <div className="overline mb-3">Access restricted</div>
          <h1 className="font-heading text-3xl mb-3" style={{ fontWeight: 500 }}>
            This page is private
          </h1>
          <p className="text-[var(--ink-2)] text-[14px] leading-relaxed">
            The Build Brief is an internal planning document. If you believe you
            should have access, please contact the platform owner.
          </p>
          <Link to="/" className="btn-ghost mt-6 inline-flex" data-testid="brief-back-home">
            <ArrowLeft size={14} /> Back to home
          </Link>
        </div>
      </div>
    );
  }

  if (!data) return <LoadingState />;

  return (
    <div className="min-h-screen">
      <TopHeader
        mode={mode}
        setMode={setMode}
        active={active}
        onJump={jump}
        onDownloadMD={onDownloadMD}
        onDownloadJSON={onDownloadJSON}
        onDownloadHTML={onDownloadHTML}
        onPrint={onPrint}
      />
      <main className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <ReportBody data={data} mode={mode} onDownloadHTML={onDownloadHTML} onPrint={onPrint} />
      </main>
    </div>
  );
}

export default Brief;
